import { useCallback, useEffect, useRef } from 'react'

export function useDebouncedCallback(
  callback: () => void | Promise<void>,
  delayMs: number
) {
  const callbackRef = useRef(callback)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isPendingRef = useRef(false)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    return () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const cancel = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    isPendingRef.current = false
  }, [])

  const schedule = useCallback(() => {
    cancel()
    isPendingRef.current = true
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      isPendingRef.current = false
      void callbackRef.current()
    }, delayMs)
  }, [cancel, delayMs])

  const flush = useCallback(async () => {
    cancel()
    await callbackRef.current()
  }, [cancel])

  const flushPending = useCallback(async () => {
    if (!isPendingRef.current) {
      return
    }
    await flush()
  }, [flush])

  return { schedule, flush, flushPending, cancel }
}
